import React from 'react';
import { View, StyleSheet, Text } from 'react-native';

import {
  TransactionItem,
  ReductionType,
} from 'apollo/queries/autogenerate/schemas';
import GradientProgress from 'shared/modules/common/components/molecules/GradientProgress';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import { formatToCurrency } from 'shared/utils/japanese';
import CreditTotal from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/CreditTotal';
import DescriptionItem from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/DescriptionItem';
import DeviceDetails from 'web/modules/common/components/organisms/AppleChargeDetailsDialog/DeviceDetails';

type Props = {
  item: TransactionItem;
  currentInstallment: number;
  totalInstallment: number;
};

export default function PostpaidUsage({
  item,
  currentInstallment,
  totalInstallment,
}: Props) {
  // Subtract each reduction from the unit price
  const totalAmount =
    item.reductions?.reduce((minuend, reduction) => {
      const difference = minuend - reduction.amount || 0;

      return difference;
    }, item.unitPrice) || item.unitPrice; // If there are no reductions, the total amount is the unit price

  const descriptions = getTransactionItemDetail({ item });

  return (
    <View style={styles.container}>
      <Text style={styles.title}>あと払いプランApple専用{`\n`}ご利用状況</Text>
      <View style={styles.progressContainer}>
        <GradientProgress progress={currentInstallment / totalInstallment} />
      </View>
      <Text style={styles.creditProgressText}>
        {currentInstallment} / {totalInstallment}回
      </Text>
      <DeviceDetails
        imageSrc={{
          uri: (item.iconImage2x as string) ?? '',
          width: 64,
          height: 64,
        }}
        deviceName={item.title ?? ''}
      />
      {item.monthlyAmount && (
        <DescriptionItem
          title="分割支払い金額"
          description={`${formatToCurrency(item.monthlyAmount)} / 月`}
        />
      )}
      {descriptions.map((purchaseItem, index) => (
        <DescriptionItem
          key={index.toString()}
          title={purchaseItem.title}
          description={purchaseItem.description}
          note={purchaseItem.note}
        />
      ))}
      <CreditTotal total={formatToCurrency(totalAmount)} />
    </View>
  );
}

type TransactionItemDetail = {
  title: string;
  description: string;
  note?: string;
};

function getTransactionItemDetail({ item }: { item: TransactionItem }) {
  const result: Array<TransactionItemDetail> = [];

  if (item.planDuration) {
    result.push({
      title: '分割金利*',
      description: `0% / ${item.planDuration}ヶ月`,
      note: '*口座振替、銀行振り込みに限ります',
    });
  }

  result.push({ title: '小計', description: formatToCurrency(item.unitPrice) });
  const description = getDescription(item);

  if (description) {
    result.push({ title: '製品詳細', description: getDescription(item) });
  }

  // ------ START DOWNPAYMENT ------
  // Downpayments only happen on the physical Apple Store
  const downPayment = item.reductions?.find(
    (reduction) => reduction.reductionType === ReductionType.DOWN_PAYMENT
  );

  if (downPayment) {
    result.push({
      title: '頭金',
      description: `${formatToCurrency(downPayment.amount)} 引き`,
    });
  }
  // ------ END DOWNPAYMENT ------

  // ------ START CARRIER DISCOUNT ------
  const carrierDiscount = item.reductions?.find(
    (reduction) => reduction.reductionType === ReductionType.CARRIER_DISCOUNT
  );

  if (carrierDiscount) {
    result.push({
      title: '通信キャリア割引',
      description: `${formatToCurrency(carrierDiscount.amount)} 引き`,
    });
  }
  // ------ END CARRIER DISCOUNT ------

  // ------ START TRADE IN ------
  // Trade-in pertains to the trade-ins that happen take place online
  // Trade-ins that happen in the physical Apple Store will be considered as a down payment (頭金)
  const tradeIn = item.reductions?.find(
    (reduction) => reduction.reductionType === ReductionType.TRADE_IN
  );

  if (tradeIn) {
    result.push({
      title: 'Apple下取り',
      description: `${formatToCurrency(tradeIn.amount)} 引き`,
    });
  }
  // ------ END TRADE IN ------

  // ------ START DISCOUNT ------
  const discount = item.reductions?.find(
    (reduction) => reduction.reductionType === ReductionType.DISCOUNT
  );

  if (discount) {
    result.push({
      title: 'Apple下取り',
      description: `${formatToCurrency(discount.amount)} 引き`,
    });
  }
  // ------ END DISCOUNT ------

  return result;
}

export function getDescription(item: TransactionItem) {
  let description = '';

  if (item.model) {
    description += `${item.model}`;
  }

  if (item.size) {
    description += `${(description ? '\n' : '') + item.size}`;
  }

  if (item.color) {
    description += `${(description ? '\n' : '') + item.color}`;
  }

  return description;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.white,
    borderRadius: 12,
    paddingTop: 24,
    paddingBottom: 40,
    paddingHorizontal: 32,
  },
  title: {
    ...typography.title02,
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 24,
    color: colors.black,
    marginBottom: 24,
  },
  creditProgressText: {
    ...typography.title03,
    fontWeight: '700',
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 26,
    color: colors.black,
  },
  progressContainer: {
    marginBottom: 12,
  },
});
